import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, MotionConfig, motion } from "motion/react";
import {
  ListMagnifyingGlassIcon,
  MoonIcon,
  PauseCircleIcon,
  PulseIcon,
  SignOutIcon,
  SunIcon,
  BuildingsIcon,
  PackageIcon,
} from "@phosphor-icons/react";
import { currentOperator, onAuthChange, signOut } from "./lib/pb.ts";
import { countHeldLeads, countProblemDeliveries } from "./lib/data.ts";
import { Login } from "./screens/Login.tsx";
import { Today } from "./screens/Today.tsx";
import { Leads } from "./screens/Leads.tsx";
import { Held } from "./screens/Held.tsx";
import { Deliveries } from "./screens/Deliveries.tsx";
import { Destinations } from "./screens/Destinations.tsx";
import { Toaster, Tooltip, type Toast } from "./components/ui.tsx";

type Route = "today" | "leads" | "held" | "deliveries" | "destinations";

const ROUTES: Array<{ id: Route; label: string; icon: React.ReactNode }> = [
  { id: "today", label: "Today", icon: <PulseIcon size={15} weight="bold" /> },
  { id: "leads", label: "Leads", icon: <ListMagnifyingGlassIcon size={15} weight="bold" /> },
  { id: "held", label: "Held", icon: <PauseCircleIcon size={15} weight="bold" /> },
  { id: "deliveries", label: "Deliveries", icon: <PackageIcon size={15} weight="bold" /> },
  { id: "destinations", label: "Destinations", icon: <BuildingsIcon size={15} weight="bold" /> },
];

function routeFromHash(): Route {
  const raw = window.location.hash.replace(/^#\/?/, "");
  return (ROUTES.find((route) => route.id === raw)?.id ?? "today") as Route;
}

/* -------------------------------------------------------------------- theme */

function useTheme() {
  const [theme, setTheme] = useState<"dark" | "light">(
    () => (document.documentElement.dataset.theme as "dark" | "light") ?? "dark",
  );

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next = current === "dark" ? "light" : "dark";
      document.documentElement.dataset.theme = next;
      try {
        localStorage.setItem("console-theme", next);
      } catch {
        /* private mode; the choice just won't persist */
      }
      return next;
    });
  }, []);

  return { theme, toggle };
}

/* -------------------------------------------------------------------- app */

export default function App() {
  const [operator, setOperator] = useState(currentOperator);
  const [route, setRoute] = useState<Route>(routeFromHash);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [counts, setCounts] = useState({ held: 0, problems: 0 });
  const { theme, toggle } = useTheme();

  useEffect(() => onAuthChange(() => setOperator(currentOperator())), []);

  useEffect(() => {
    const onHash = () => setRoute(routeFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  /* Badge counts in the nav, so what needs attention is visible from any screen. */
  const refreshCounts = useCallback(async () => {
    if (!currentOperator()) return;
    try {
      const [held, problems] = await Promise.all([countHeldLeads(), countProblemDeliveries()]);
      setCounts({ held, problems });
    } catch {
      /* the screens surface their own errors; the badge just stays put */
    }
  }, []);

  const notify = useCallback(
    (tone: "ok" | "bad", text: string) => {
      const id = Date.now() + Math.random();
      setToasts((current) => [...current, { id, tone, text }]);
      setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4200);
      // An action just changed what needs attention, so the nav counts are now
      // stale. A badge that disagrees with the screen is worse than no badge.
      void refreshCounts();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );


  useEffect(() => {
    if (!operator) return;
    void refreshCounts();
    const timer = setInterval(refreshCounts, 60_000);
    return () => clearInterval(timer);
  }, [operator, route, refreshCounts]);

  if (!operator) return <Login />;

  return (
    /* Every CSS transition here ships a `prefers-reduced-motion` guard, but the
       motion library defaults to `reducedMotion: "never"`, so the row and toast
       animations were ignoring the OS setting entirely. `"user"` makes the JS
       half honour it too, which is what DESIGN.md already claims happens. */
    <MotionConfig reducedMotion="user">
      <div className="shell">
        <header className="topbar">
          <a className="brand" href="#/today" aria-label="Pipeline — go to Today">
            <span className="brand__mark" aria-hidden="true">
              P
            </span>
            <span className="brand__name">Pipeline</span>
          </a>

          <nav className="nav" aria-label="Sections">
            {ROUTES.map((item) => {
              const count =
                item.id === "held" ? counts.held : item.id === "deliveries" ? counts.problems : 0;
              return (
                <a
                  key={item.id}
                  className="nav__item"
                  href={`#/${item.id}`}
                  aria-current={route === item.id ? "page" : undefined}
                >
                  {item.icon}
                  {item.label}
                  {count > 0 && (
                    <span
                      className="nav__count"
                      data-tone={item.id === "deliveries" ? "bad" : "warn"}
                      aria-label={`${count} needing attention`}
                    >
                      {count}
                    </span>
                  )}
                </a>
              );
            })}
          </nav>

          <div className="topbar__end">
            {/* Custom tooltips, so a hint about the control appears in ~150ms
                instead of the browser's own second-long `title` delay. */}
            <Tooltip text={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}>
              <button
                className="icon-btn"
                onClick={toggle}
                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              >
                {/* Both icons stay mounted and cross-fade in the same slot. The
                    theme flip is a real state change; the icon following it
                    rather than jumping is the whole point. */}
                <span
                  className="t-icon-swap"
                  data-state={theme === "dark" ? "a" : "b"}
                  aria-hidden="true"
                >
                  <span className="t-icon" data-icon="a">
                    <SunIcon size={16} />
                  </span>
                  <span className="t-icon" data-icon="b">
                    <MoonIcon size={16} />
                  </span>
                </span>
              </button>
            </Tooltip>
            <Tooltip text={`Sign out (${operator.email})`}>
              <button className="icon-btn" onClick={signOut} aria-label="Sign out">
                <SignOutIcon size={16} />
              </button>
            </Tooltip>
          </div>
        </header>

        <main className="main">
          {/* A route change is a state change worth following — the whole
              surface is replaced, and without a transition the swap reads as a
              flicker. `mode="wait"` so the outgoing screen is gone before the
              next arrives; two screens cross-fading over each other at
              different heights makes the page jump.

              `initial={false}` keeps this off the first paint: arriving at the
              app is not a state change, and animating it would be exactly the
              page-load choreography DESIGN.md rules out. Exit is quicker than
              enter so the swap never feels like waiting. */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={route}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -3, transition: { duration: 0.11 } }}
              transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            >
              {route === "today" && <Today />}
              {route === "leads" && <Leads />}
              {route === "held" && <Held notify={notify} />}
              {route === "deliveries" && <Deliveries notify={notify} />}
              {route === "destinations" && <Destinations notify={notify} />}
            </motion.div>
          </AnimatePresence>
        </main>

        <Toaster toasts={toasts} onUndo={() => {}} />
        <div className="grain" aria-hidden="true" />
      </div>
    </MotionConfig>
  );
}
